import { startOfMonth, endOfMonth, subMonths, format } from "date-fns";
import { getFYBounds as coreGetFYBounds, type DateRange } from "@core";

export type { DateRange };

/** Local (never UTC) `YYYY-MM-DD` — `toISOString()` would shift IST dates. */
function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Get Indian Financial Year bounds for a given date.
 * Indian FY runs from April to March. The actual rule lives in `@core`
 * (string arithmetic, no `Date` object) — this wrapper only adapts the
 * `Date`-based call sites this module still has and adds the display label.
 */
export function getFYBounds(date: Date = new Date()) {
  const { start, end } = coreGetFYBounds(toYmd(date));
  const year = Number(start.slice(0, 4));
  return {
    start,
    end,
    label: `FY ${year}-${String(year + 1).slice(2)}`,
  };
}

/**
 * Get current month start and end as YYYY-MM-DD strings.
 */
export function getCurrentMonthBounds() {
  const now = new Date();
  return {
    start: format(startOfMonth(now), "yyyy-MM-dd"),
    end: format(endOfMonth(now), "yyyy-MM-dd"),
  };
}

/**
 * Get last 6 months with boundaries as YYYY-MM-DD strings.
 */
export function getLast6Months(): { month: string; start: string; end: string }[] {
  const result = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = subMonths(now, i);
    result.push({
      month: format(d, "MMM yyyy"),
      start: format(startOfMonth(d), "yyyy-MM-dd"),
      end: format(endOfMonth(d), "yyyy-MM-dd"),
    });
  }
  return result;
}

/**
 * Convert YYYY-MM-DD to DD/MM/YYYY for display.
 */
export function formatDateDisplay(dateStr: string): string {
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
}
